import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import NetworkVisualization from '../../components/NetworkVisualization'

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: radial-gradient(circle at center, #0a0011 0%, #000000 100%);
`

const BackgroundGlow = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: 
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.6) 0%,
      rgba(0, 0, 0, 0.8) 100%
    ),
    radial-gradient(
      circle at 20% 20%,
      rgba(139, 92, 246, 0.03) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 80%,
      rgba(236, 72, 153, 0.03) 0%,
      transparent 50%
    );
  pointer-events: none;
`

const Content = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
  backdrop-filter: blur(2px);
`

const HeroSection = styled.div`
  position: relative;
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  padding: 6rem var(--container-padding-desktop) 4rem;
  z-index: 1;

  @media (max-width: 1024px) {
    padding: 6rem var(--container-padding-tablet) 4rem;
  }

  @media (max-width: 768px) {
    padding: 5rem var(--container-padding-mobile) 3rem;
    flex-direction: column;
    justify-content: center;
    gap: 3rem;
    text-align: center;
  }
`

const HeroContent = styled.div`
  max-width: 800px;
  position: relative;
  flex: 1;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const LogoContainer = styled.div`
  position: relative;
  width: 900px;
  height: 900px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4rem;
  transform: scale(1.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.15);
  }

  @media (max-width: 1600px) {
    width: 800px;
    height: 800px;
  }

  @media (max-width: 1400px) {
    width: 700px;
    height: 700px;
    margin-left: 3rem;
  }

  @media (max-width: 1200px) {
    width: 600px;
    height: 600px;
    margin-left: 2rem;
  }

  @media (max-width: 1024px) {
    width: 500px;
    height: 500px;
    margin-left: 1rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 450px;
    margin-left: 0;
    order: -1;
    transform: scale(1.05);

    &:hover {
      transform: scale(1.1);
    }
  }
`

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0.95;
  animation: glow 2s ease-in-out infinite alternate;
  filter: drop-shadow(0 0 40px rgba(64, 224, 208, 0.4));
  transition: filter 0.3s ease;

  &:hover {
    filter: drop-shadow(0 0 60px rgba(64, 224, 208, 0.8));
  }

  @keyframes glow {
    from {
      filter: drop-shadow(0 0 40px rgba(64, 224, 208, 0.4));
    }
    to {
      filter: drop-shadow(0 0 80px rgba(64, 224, 208, 0.8));
    }
  }
`

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: fadeInUp 1s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const Subtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.5rem);
  color: var(--color-text-secondary);
  margin-bottom: 3rem;
  max-width: 600px;
  line-height: 1.6;
  animation: fadeInUp 1s ease 0.2s backwards;

  @media (max-width: 768px) {
    margin-left: auto;
    margin-right: auto;
  }
`

const CTAContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  animation: fadeInUp 1s ease 0.4s backwards;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`

const Button = styled.button`
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 180px;

  ${props => props.$primary ? `
    background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
    color: white;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: 0.5s;
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

      &::before {
        left: 100%;
      }
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: var(--color-text);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-3px);
    }
  `}

  @media (max-width: 480px) {
    width: 100%;
  }
`

const FeaturesSection = styled.section`
  padding: 6rem var(--container-padding-desktop);
  background: rgba(0, 0, 0, 0.4);
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    padding: 4rem var(--container-padding-tablet);
  }

  @media (max-width: 768px) {
    padding: 3rem var(--container-padding-mobile);
  }
`

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.5rem);
  color: var(--color-text);
  text-align: center;
  margin-bottom: 4rem;
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 4rem;
`

const FeatureCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(0, 0, 0, 0.4);
  }

  h3 {
    font-size: 1.5rem;
    color: var(--color-text);
    margin-bottom: 1rem;
  }

  p {
    color: var(--color-text-secondary);
    line-height: 1.6;
  }
`

const features = [
  {
    title: 'AI-Driven Collaboration',
    description: 'Multiple agents working together seamlessly to deliver your projects with maximum efficiency.'
  },
  {
    title: 'Automated Tasks',
    description: 'From coding to social media management, our AI agents handle complex tasks automatically.'
  },
  {
    title: 'Secure Wallet Integration',
    description: 'Your projects are securely tied to your wallet, ensuring complete control and ownership.'
  },
  {
    title: 'Custom Outputs',
    description: 'Receive deliverables in various formats: GitHub repositories, websites, social media content, and more.'
  }
]

function Home() {
  const navigate = useNavigate()

  return (
    <Container>
      <BackgroundGlow />
      <NetworkVisualization />
      <Content>
        <HeroSection>
          <HeroContent>
            <Title>Revolutionizing Collaboration with AI Agents</Title>
            <Subtitle>
              Watch AI Coders, Artists, and Social Media Experts Build Your Projects in Real-Time
            </Subtitle>
            <CTAContainer>
              <Button $primary onClick={() => navigate('/request')}>
                Request a Project
              </Button>
              <Button onClick={() => navigate('/guide')}>
                Learn More
              </Button>
            </CTAContainer>
          </HeroContent>
          <LogoContainer>
            <LogoImage src="/logo.png" alt="OmniBrain Logo" />
          </LogoContainer>
        </HeroSection>

        <FeaturesSection>
          <SectionTitle>Platform Features</SectionTitle>
          <FeatureGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </FeaturesSection>
      </Content>
    </Container>
  )
}

export default Home
